import { useEffect, useId, useState } from 'react'
import { Copy, Download, Share } from 'lucide-react'
import bunny from '../assets/bunny-mask.png'
import {
  canvasToBlob,
  renderShareCard,
  SHARE_FORMATS,
  SHARE_TEMPLATES,
  shareFileName,
  type ShareCardContent,
  type ShareFormatId,
} from '../lib/shareCard'
import { useToast } from './Toast'
import { btn, labelCls, useBackdropClose } from './ui'

// "Share your writing": the step after publishing (and the author's own
// "Share as image" on their published page). A ready-made image of the
// writing in a few designs and sizes; the preview is the exported PNG itself.
// The designs live in lib/shareCard.ts (SHARE_TEMPLATES), to be swapped.

interface Props {
  content: ShareCardContent
  /** The writing's link, for "Copy link" and the share sheet. */
  url: string
  /** "Published" right after publishing; else the plain title. */
  justPublished?: boolean
  /** Right after republishing an update: "Updated" instead. */
  updated?: boolean
  onClose: () => void
}

export function ShareWritingSheet({ content, url, justPublished = false, updated = false, onClose }: Props) {
  const toast = useToast()
  const backdrop = useBackdropClose(onClose)
  const titleId = useId()
  const [templateId, setTemplateId] = useState(SHARE_TEMPLATES[0].id)
  const [formatId, setFormatId] = useState<ShareFormatId>('portrait')
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [unsupported, setUnsupported] = useState(false)
  const template = SHARE_TEMPLATES.find((t) => t.id === templateId) ?? SHARE_TEMPLATES[0]
  const format = SHARE_FORMATS.find((f) => f.id === formatId) ?? SHARE_FORMATS[0]

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    let alive = true
    void renderShareCard(content, template, format, bunny).then((c) => {
      if (!alive) return
      setCanvas(c)
      setUnsupported(!c)
      let src: string | null = null
      try {
        src = c?.toDataURL('image/png') ?? null
      } catch {
        src = null
      }
      setPreview(src)
    })
    return () => {
      alive = false
    }
  }, [content, template, format])

  const fileName = shareFileName(content.title, format.id)

  async function download() {
    if (!canvas) return
    const blob = await canvasToBlob(canvas)
    if (!blob) return toast('Couldn’t make the image in this browser.')
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(href), 4000)
    toast('Image saved')
  }

  async function copyImage() {
    if (!canvas) return
    try {
      const blob = await canvasToBlob(canvas)
      if (!blob || typeof ClipboardItem === 'undefined') throw new Error('unsupported')
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
      toast('Image copied')
    } catch {
      toast('This browser can’t copy images — download it instead.')
    }
  }

  async function shareToApps() {
    if (!canvas) return
    const blob = await canvasToBlob(canvas)
    const file = blob ? new File([blob], fileName, { type: 'image/png' }) : null
    try {
      if (file && navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: content.title, text: url })
      else if (navigator.share) await navigator.share({ title: content.title, url })
    } catch {
      /* dismissed */
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url)
      toast('Link copied')
    } catch {
      toast(url)
    }
  }

  const canShareApps = typeof navigator !== 'undefined' && !!navigator.share
  const ready = !!canvas

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <button type="button" aria-label="Close" onClick={onClose} className="fixed inset-0 bg-foreground/32" />
      <div {...backdrop} className="relative flex min-h-full items-start justify-center px-4 py-6 sm:py-10">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="relative flex w-full max-w-[560px] flex-col gap-5 rounded-2xl bg-card p-6 font-sans shadow-[var(--shadow-sheet)] sm:p-7"
        >
          <div className="flex flex-col gap-1">
            <h2 id={titleId} className="m-0 font-display text-[24px] font-normal text-primary">
              {justPublished ? (updated ? 'Updated. Share your writing' : 'Published. Share your writing') : 'Share your writing'}
            </h2>
            <p className="m-0 text-[13px]/[1.5] text-muted-foreground">A card made from your writing, ready to post. Pick a look and a size.</p>
          </div>

          <div className="flex justify-center rounded-xl bg-background p-4">
            {preview ? (
              <img
                src={preview}
                alt={`Share card: ${content.title}`}
                className="max-h-[46vh] w-auto max-w-full rounded-lg shadow-[var(--shadow-chip)]"
                style={{ aspectRatio: `${format.width} / ${format.height}` }}
              />
            ) : (
              <div
                role="status"
                className="flex max-h-[46vh] w-[240px] items-center justify-center rounded-lg border border-dashed border-border px-4 text-center font-display text-[14px] italic text-muted-foreground"
                style={{ aspectRatio: `${format.width} / ${format.height}` }}
              >
                {unsupported ? 'This browser can’t draw the card.' : 'Drawing the card…'}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
              <legend className={`${labelCls} mb-1.5`}>LOOK</legend>
              <div className="flex flex-wrap gap-1.5">
                {SHARE_TEMPLATES.map((t) => {
                  const on = t.id === template.id
                  return (
                    <label
                      key={t.id}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] font-medium ${on ? 'border-[1.5px] border-primary bg-accent/18 text-primary' : 'border border-border/80 text-foreground hover:bg-foreground/3'}`}
                    >
                      <input type="radio" name="share-look" value={t.id} checked={on} onChange={() => setTemplateId(t.id)} className="sr-only" />
                      <span aria-hidden className="size-3.5 rounded-full border border-border" style={{ background: t.palette.background }} />
                      {t.label}
                    </label>
                  )
                })}
              </div>
            </fieldset>
            <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
              <legend className={`${labelCls} mb-1.5`}>SIZE</legend>
              <div className="flex flex-wrap gap-1.5">
                {SHARE_FORMATS.map((f) => {
                  const on = f.id === format.id
                  return (
                    <label
                      key={f.id}
                      title={f.hint}
                      className={`cursor-pointer rounded-lg px-2.5 py-1.5 text-[13px] font-medium ${on ? 'border-[1.5px] border-primary bg-accent/18 text-primary' : 'border border-border/80 text-foreground hover:bg-foreground/3'}`}
                    >
                      <input type="radio" name="share-size" value={f.id} checked={on} onChange={() => setFormatId(f.id)} className="sr-only" />
                      {f.label}
                    </label>
                  )
                })}
              </div>
            </fieldset>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <button type="button" onClick={copyLink} className={`${btn.outline} mr-auto`}>
              Copy link
            </button>
            <button type="button" onClick={copyImage} disabled={!ready} className={btn.outlineLg}>
              <Copy size={15} aria-hidden />
              Copy image
            </button>
            {canShareApps && (
              <button type="button" onClick={shareToApps} disabled={!ready} className={btn.outlineLg}>
                <Share size={15} aria-hidden />
                Share
              </button>
            )}
            <button type="button" onClick={download} disabled={!ready} className={btn.primaryLg}>
              <Download size={15} aria-hidden />
              Download
            </button>
          </div>
          <button type="button" onClick={onClose} className="-mt-2 self-center text-[13px] font-medium text-muted-foreground hover:text-primary">
            {justPublished ? 'Skip for now' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  )
}
